/** The slice of a LiveKit `TrackReference` the audio selection reads. */
export interface AudioTrackCandidate {
  participant: {
    identity: string;
    isLocal: boolean;
    isScreenShareEnabled: boolean;
  };
}

export interface AudibilityRules {
  /** Proximity bubble: governs whose microphone we play. */
  isInProximity: (identity: string) => boolean;
  /**
   * Whose screen share we are shown (`useVirtualRoomFilteredParticipants`). Shared
   * tab audio follows the picture: if you can watch someone's share you can hear
   * it, even when they are outside your proximity bubble (conference rooms).
   */
  canSeeScreenShare: (identity: string) => boolean;
}

/**
 * Remote audio tracks to render. A participant's microphone and their shared tab
 * audio play together; the local participant is never played back (feedback).
 */
export function selectAudibleRemoteAudioTracks<T extends AudioTrackCandidate>(
  microphoneTracks: readonly T[],
  screenShareAudioTracks: readonly T[],
  rules: AudibilityRules
): T[] {
  const audibleMicrophones = microphoneTracks.filter(
    (track) => !track.participant.isLocal && rules.isInProximity(track.participant.identity)
  );

  const audibleScreenShareAudio = screenShareAudioTracks.filter((track) => {
    const { identity, isLocal, isScreenShareEnabled } = track.participant;
    if (isLocal || !isScreenShareEnabled) return false;
    return rules.isInProximity(identity) || rules.canSeeScreenShare(identity);
  });

  return [...audibleMicrophones, ...audibleScreenShareAudio];
}
